<template>

  <!--
    P2 (2026-09-19): four states, decided from accountStatus and subscriptionEndsAt.

      past_due                         -> "Update card"        (Stripe's portal, card flow only)
      active, subscriptionEndsAt set   -> PENDING END: the end date + "Keep subscription"
      active, nothing scheduled        -> "End subscription"
      anything else                    -> "Subscribe"

    "Update card" is offered in all three subscribed states, not only past_due
    (follow-up, 2026-09-19): cards expire while a subscription is healthy. In
    past_due it is the primary action; otherwise it sits under the primary one.

    Layout: this renders inside the plan card's right-hand action column
    (OrgSettings .os-plan-right), which does not shrink, so it renders BUTTONS
    ONLY. The pending-end sentence ("Your subscription ends on ...") used to be
    here; the column grew to its full unwrapped width and the card body went to
    0px (measured in Chromium). It was never an overlay, since nothing here is
    positioned. The sentence now lives in the card body (OrgSettings,
    src/utils/subscriptionEndsOn.js).

    The pending-end state is a state of its own, not a variant of subscribed. A
    subscription scheduled to end stays `active` in Stripe until the boundary, so
    before P2 this component offered the customer who had just ended their
    subscription the same button again. Wording follows D5: the subscription ENDS
    ON A DATE. It is never described as "cancelled".
  -->
  <template v-if="!isLoading">
    <button v-if="billingState === 'past_due'" @click="updateCard" class="button-green co-update-card">Update card</button>

    <button v-else-if="billingState === 'ending'" @click="keepSubscription" class="button-green co-keep">Keep subscription</button>

    <button v-else-if="billingState === 'active'" @click="confirmUnsubscribe" class="button-red co-end">End subscription</button>

    <button v-else @click="subscribe" class="button-green co-subscribe">Subscribe</button>

    <!-- Its own v-if, AFTER the chain above: placed inside it, the v-else would
         attach to this button and Subscribe would render for past_due. -->
    <button v-if="billingState === 'ending' || billingState === 'active'" @click="updateCard"
      class="co-update-card co-update-card-secondary">Update card</button>
  </template>
  <button disabled v-if="isLoading" class="button-green">Loading</button>

  <p v-if="errorMessage" class="co-error" role="alert">{{ errorMessage }}</p>

  <Spinner :visible="isLoading" />

  <!-- Confirmation Popup -->
  <div v-if="showConfirmPopup" class="popup-container" @click.self="cancelUnsubscribe">
    <div class="popup-content dialog-confirm">
      <div class="d-header">
        <div class="d-icon-wrap" style="background:#fef3c7;">
          <svg viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div class="d-header-text">
          <div class="d-title">End your subscription?</div>
          <div class="d-body-text">It will end at the end of your current billing period. Everything keeps working until then, and you can keep your subscription at any time before that date.</div>
        </div>
      </div>
      <div class="d-footer right">
        <button v-if="closeButton != true" class="dbtn dbtn-cancel" @click="cancelUnsubscribe">Not now</button>
        <button class="dbtn dbtn-amber co-confirm-end" @click="unsubscribe">End subscription</button>
      </div>
    </div>
  </div>

</template>

<script>
import { ref, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { token } from '@/stores/token';
import Spinner from "@/components/Spinner.vue";
import { useSubscriptionActions } from "@/composables/useSubscriptionActions";
import { toDate } from "@/utils/subscriptionEndsOn";

export default {
  components: { Spinner },
  props: ["accountStatus", "subscriptionEndsAt", "closeButton", "organisationName",
    "address", "city", "state", "postcode", "abnAcn"],
  emits: ["subscriptionSuccess"],
  setup(props, { emit }) {
    const { user } = useAuth0();
    const store = token();
    const showConfirmPopup = ref(false);
    // End, Keep and Update card come from the shared module, the same functions
    // the Navbar banner calls. isLoading and errorMessage are shared with
    // subscribe() below so there is one spinner and one error line.
    const {
      isLoading, errorMessage, endSubscription, keepSubscription, updateCard, postJson,
    } = useSubscriptionActions({ onChanged: () => emit("subscription-success") });

    const endsOn = computed(() => toDate(props.subscriptionEndsAt));

    // Named billingState, not `state`: `state` is already a prop (the address state).
    const billingState = computed(() => {
      if (props.accountStatus === 'past_due') return 'past_due';
      if (props.accountStatus === 'active') return endsOn.value ? 'ending' : 'active';
      return 'subscribe';
    });

    const subscribe = async () => {
      if (!user) {
        console.error('No authenticated user found.');
        return;
      }
      errorMessage.value = '';

      const userId = user.value.sub;
      const email = user.value.email;
      const organisationId = store.organisationId;

      try {
        isLoading.value = true;
        const { ok, result } = await postJson('createCheckoutSession', { userId, email, organisationId });

        if (!ok || !result.url) {
          // This used to go to the console only, and the button did nothing
          // (tickets/retired/ticket-conversion-path-checkout-results-unreachable.md, step 4).
          console.error('Error creating checkout session:', result.error || result);
          errorMessage.value = result.error || 'We could not start checkout. Please try again.';
          return;
        }

        // Redirect to Stripe's hosted Checkout. This leaves the SPA; Stripe returns
        // the user to the session's success_url / cancel_url (/checkout-results),
        // which finalises the subscription. No Stripe.js or publishable key needed.
        window.location.href = result.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        errorMessage.value = 'We could not start checkout. Please try again.';
      } finally {
        isLoading.value = false;
      }
    };

    const confirmUnsubscribe = () => {
      errorMessage.value = '';
      showConfirmPopup.value = true;
    };

    const cancelUnsubscribe = () => {
      showConfirmPopup.value = false;
    };

    // Confirmed from the dialog. Before P2 this awaited a CLIENT write of
    // accountStatus that firestore.rules (correctly) refuse, so the spinner never
    // stopped (tickets/retired/ticket-unsubscribe-client-write-hangs.md). The shared module has
    // no client write, and every failure ends the spinner and says why.
    const unsubscribe = () => {
      showConfirmPopup.value = false;
      return endSubscription();
    };

    return {
      billingState, errorMessage,
      subscribe, confirmUnsubscribe, cancelUnsubscribe, unsubscribe, keepSubscription, updateCard,
      isLoading, showConfirmPopup,
    };
  },
};
</script>

<style scoped>
.button-group {
  justify-content: center;
}
.co-update-card-secondary {
  display: block;
  margin: 8px 0 0 auto;
  padding: 0;
  background: none;
  border: none;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: underline;
  cursor: pointer;
}
.co-update-card-secondary:hover {
  color: rgba(255, 255, 255, 0.9);
}
/* The plan card's action column does not shrink, so a sentence here must wrap
   inside a bounded width or it widens the column and crushes the card body:
   the pending-end layout defect, in its error-message form. */
.co-error {
  max-width: 180px;
  margin: 8px 0 0;
  font-size: 13px;
  color: #b91c1c;
}
</style>
